import type { FunctionComponent } from "react";
import { useCallback } from "react";
import styled, { css } from "styled-components";
import { IconButton, Button, ButtonLabel } from "@similarweb/ui-components";
import type { IconName } from "@similarweb/icons";
import { SWReactIcons } from "@similarweb/icons";
import { CenteredFlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";

export enum EErrorTypes {
    NO_DATA = "no-data-lab",
    TIME_PERIOD_NOT_SUPPORTED = "no-data-lab-2",
    ERROR = "no-data-tree",
}

const EmptyStateWrapper = styled.div<{ isFullPage?: boolean }>`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    ${({ isFullPage }) =>
        isFullPage &&
        css`
            align-items: center;
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
        `}
`;

const EmptyStateButtonWrapper = styled.div`
    margin-top: 16px;
`;

const EmptyStateContentWrapper = styled(CenteredFlexColumn)`
    margin-top: 16px;
`;

const TitleWrapper = styled.div`
    margin-top: 17px;
    ${setFontVariant("subtitle1", colorsPalettes.carbon[500])};
`;

const SubTitleWrapper = styled.div`
    ${setFontVariant("caption", colorsPalettes.carbon[300])};
`;

interface EmptyStateProps {
    title: any;
    subtitle?: any;
    type?: EErrorTypes;
}

interface GenericEmptyStateProps extends EmptyStateProps {
    isFullPage?: boolean;
    btnLabel?: string;
    onPrimaryBrnClick?: VoidFunction;
    iconName?: IconName;
}

const StyledIcon = styled(SWReactIcons)<{ height?: number; width?: number }>`
    ${({ width = 150 }) => `width:${width}px`};
`;

const EmptyState: FunctionComponent<EmptyStateProps> = ({ title, subtitle, type }) => {
    return (
        <EmptyStateContentWrapper>
            <StyledIcon
                iconName={type}
                data-automation-icon-name="no-data-lab"
            />
            <TitleWrapper data-automation-title-name="no-result-title">{title}</TitleWrapper>
            <SubTitleWrapper data-automation-subtitle-name="no-result-subtitle">{subtitle}</SubTitleWrapper>
        </EmptyStateContentWrapper>
    );
};

export const GenericEmptyState: FunctionComponent<GenericEmptyStateProps> = ({
    title,
    subtitle,
    btnLabel,
    onPrimaryBrnClick,
    type = EErrorTypes.NO_DATA,
    isFullPage,
    iconName,
}) => {
    const handleBtnClick = useCallback(() => {
        onPrimaryBrnClick();
    }, []);

    return (
        <EmptyStateWrapper
            isFullPage={isFullPage}
            data-automation="no-data-wrapper"
        >
            <EmptyState
                title={title}
                subtitle={subtitle}
                type={type}
            />
            {btnLabel ? (
                <EmptyStateButtonWrapper>
                    {iconName ? (
                        <IconButton
                            onClick={handleBtnClick}
                            type="primary"
                            iconName={iconName}
                        >
                            <ButtonLabel>{btnLabel}</ButtonLabel>
                        </IconButton>
                    ) : (
                        <Button
                            onClick={handleBtnClick}
                            type="primary"
                        >
                            <ButtonLabel>{btnLabel}</ButtonLabel>
                        </Button>
                    )}
                </EmptyStateButtonWrapper>
            ) : null}
        </EmptyStateWrapper>
    );
};
